import { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";
import { lazyWithRetry } from "./lib/lazy-with-retry";

// Eagerly load the homepage for fastest initial paint
import Index from "./pages/Index";

// Lazy-load all other pages (with stale-chunk retry/reload)
const About = lazyWithRetry(() => import("./pages/About"));
const Services = lazyWithRetry(() => import("./pages/Services"));
const ServiceDetail = lazyWithRetry(() => import("./pages/ServiceDetail"));
const Careers = lazyWithRetry(() => import("./pages/Careers"));
const JobDetail = lazyWithRetry(() => import("./pages/JobDetail"));
const Application = lazyWithRetry(() => import("./pages/Application"));
const Blog = lazyWithRetry(() => import("./pages/Blog"));
const BlogPost = lazyWithRetry(() => import("./pages/BlogPost"));
const Calculator = lazyWithRetry(() => import("./pages/Calculator"));
const Contact = lazyWithRetry(() => import("./pages/Contact"));
const GoldenDome = lazyWithRetry(() => import("./pages/GoldenDome"));
const AdminLogin = lazyWithRetry(() => import("./pages/AdminLogin"));
const Admin = lazyWithRetry(() => import("./pages/Admin"));
const AdminJobs = lazyWithRetry(() => import("./pages/AdminJobs"));
const AdminJobForm = lazyWithRetry(() => import("./pages/AdminJobForm"));
const AdminBlog = lazyWithRetry(() => import("./pages/AdminBlog"));
const AdminBlogForm = lazyWithRetry(() => import("./pages/AdminBlogForm"));
const AdminApplications = lazyWithRetry(() => import("./pages/AdminApplications"));
const AdminScholarships = lazyWithRetry(() => import("./pages/AdminScholarships"));
const AdminCMMC = lazyWithRetry(() => import("./pages/AdminCMMC"));
const AdminSecurity = lazyWithRetry(() => import("./pages/AdminSecurity"));
const AdminPipeline = lazyWithRetry(() => import("./pages/AdminPipeline"));
const AdminInquiries = lazyWithRetry(() => import("./pages/AdminInquiries"));
const AdminReferrals = lazyWithRetry(() => import("./pages/AdminReferrals"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="flex min-h-[60vh] items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

const App = () => (
  <MotionConfig reducedMotion="user">
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/careers/:id" element={<JobDetail />} />
            <Route path="/application" element={<Application />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/calculator" element={<Calculator />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/golden-dome" element={<GoldenDome />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
            <Route path="/admin/applications" element={<ProtectedRoute><AdminApplications /></ProtectedRoute>} />
            <Route path="/admin/scholarships" element={<ProtectedRoute><AdminScholarships /></ProtectedRoute>} />
            <Route path="/admin/jobs" element={<ProtectedRoute><AdminJobs /></ProtectedRoute>} />
            <Route path="/admin/jobs/new" element={<ProtectedRoute><AdminJobForm /></ProtectedRoute>} />
            <Route path="/admin/jobs/:id/edit" element={<ProtectedRoute><AdminJobForm /></ProtectedRoute>} />
            <Route path="/admin/blog" element={<ProtectedRoute><AdminBlog /></ProtectedRoute>} />
            <Route path="/admin/blog/new" element={<ProtectedRoute><AdminBlogForm /></ProtectedRoute>} />
            <Route path="/admin/blog/:id/edit" element={<ProtectedRoute><AdminBlogForm /></ProtectedRoute>} />
            <Route path="/admin/cmmc" element={<ProtectedRoute><AdminCMMC /></ProtectedRoute>} />
            <Route path="/admin/security" element={<ProtectedRoute><AdminSecurity /></ProtectedRoute>} />
            <Route path="/admin/pipeline" element={<ProtectedRoute><AdminPipeline /></ProtectedRoute>} />
            <Route path="/admin/inquiries" element={<ProtectedRoute><AdminInquiries /></ProtectedRoute>} />
            <Route path="/admin/referrals" element={<ProtectedRoute><AdminReferrals /></ProtectedRoute>} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </MotionConfig>
);

export default App;
